import React from 'react';
import Logo from 'components/shared/Logo/Logo';
import ThemedText from 'components/shared/ThemedText/ThemedText';
import UnlockedText from 'components/shared/UnlockedText/UnlockedText';
import { InlineStyledText } from 'components/shared/InlineStyledText/InlineStyledText';
import YouTubeEmbed from 'components/shared/YouTubeEmbed/YouTubeEmbed';
import { EmbeddedNode } from 'components/shared/RichTextTable/types';
import { Node as ContentfulNode } from '@contentful/rich-text-types';
import { ContentfulRichTextBlock, DocumentWithEmbeddedEntries } from 'libs/contentful/types';
import CTA, { isValidCta } from 'components/shared/CTA/CTA';
import { getCurrentPageDisclosureNumberingMap } from 'utils/disclosures';
import RichTextInline from 'components/shared/RichTextInline/RichTextInline';
import { renderSuperscript } from 'utils/superscriptUtils';
import Linebreak from 'components/shared/Linebreak/Linebreak';
import BaseModal from '../shared/BaseModal/BaseModal';
import { fromAny } from '@total-typescript/shoehorn';

// Map of disclosure sys.id to number for inline disclosure numbering
export type DisclosureNumberingMap = Map<string, number>;

const renderEmbeddedEntry = (
  node: ContentfulNode,
  children: React.ReactNode,
  content: DocumentWithEmbeddedEntries,
  disclosureNumberingMap?: DisclosureNumberingMap,
) => {
  const embeddedNode = node as EmbeddedNode;
  const inlineEntries = content?.links?.entries?.inline || [];
  const entry = inlineEntries.find(i => i.sys?.id === embeddedNode.data.target.sys.id);

  if (!entry) return null;

  // change: lower case this value to make it compatible with
  // experiments
  switch (entry.__typename) {
    case 'Logo':
      return <Logo image={entry.image!} __typename="Logo" />;
    case 'Cta':
      return isValidCta(entry) ? (
        <span className="inline-block max-w-full" data-embedded-cta>
          <CTA cta={entry} />
        </span>
      ) : null;
    case 'ThemedText':
      return (
        <ThemedText
          themedText={fromAny<DocumentWithEmbeddedEntries, unknown>(entry.themedText)}
          __typename="ThemedText"
        />
      );
    case 'UnlockedText':
      return (
        <UnlockedText
          unlockedText={fromAny<DocumentWithEmbeddedEntries, unknown>(entry.unlockedText)}
          __typename="unlockedtext"
        />
      );
    case 'InlineStyledText':
      return <InlineStyledText content={entry.content} textStyle={entry.textStyle} __typename="InlineStyledText" />;
    case 'YouTubeEmbed':
      return <YouTubeEmbed youTubeVideoUrl={entry.youTubeVideoUrl!} __typename="YouTubeEmbed" />;
    case 'Disclosure':
      // For embedded disclosures, show the symbol or number as a superscript
      // Use provided map or fall back to current page map
      const numberingMap = disclosureNumberingMap || getCurrentPageDisclosureNumberingMap();
      const disclosureNumber = numberingMap?.get(entry.sys?.id || '');
      const symbol = entry?.symbol;
      const number = disclosureNumber?.toString();

      // Only render superscript if we have a valid symbol or number
      const marker = symbol || number;
      if (marker) {
        return (
          <span className="font-inherit">
            {/* Set position in HTML so hidden text does not affect table columns on first paint. */}
            <span className="sr-only select-none inline-block" style={{ position: 'fixed' }}>
              {` (see disclosure ${marker}) `}
            </span>
            <span aria-hidden>{renderSuperscript(marker)}</span>
          </span>
        );
      }

      // If no symbol or number, don't render anything
      return null;
    case 'Modal':
      return (
        <BaseModal
          title={entry.headline!}
          body={entry.copy!}
          openButtonText={entry.disclaimerText!}
          iconPosition={entry.iconPosition}
          iconOnlyLink={entry.iconOnlyLink}
        />
      );
    case 'Shortcode':
      return <RichTextInline inline={entry.shortCodeValue as ContentfulRichTextBlock} />;
    case 'Linebreak':
      return <Linebreak breakBehavior={entry.breakBehavior!} testId={entry.testId} />;
    default:
      return null;
  }
};

export default renderEmbeddedEntry;
